import { defineStore } from 'pinia'
import PipelineService from '@/services/pipeline.service'
import type { PipelineStage, PipelineLead, ReorderStageItem } from '@/types/pipeline'
import { toast } from 'vue3-toastify'

interface CardForm {
  name: string
  interest: string
  estimated_value: number
  notes: string
  pipeline_stage_id: number
  other_interest?: string
}

interface PipelineState {
  stages: PipelineStage[]
  loading: boolean
  draggingLead: PipelineLead | null
  draggingFromStageId: number | null
  cardForm: CardForm
}

export const usePipelineStore = defineStore('pipeline', {
  state: (): PipelineState => ({
    stages: [],
    loading: false,
    draggingLead: null,
    draggingFromStageId: null,
    cardForm: {
      name: '',
      interest: '',
      estimated_value: 0,
      notes: '',
      pipeline_stage_id: 0,
      other_interest: ''
    },
  }),

  actions: {
    errorMessage(error: any, fallback: string): string {
      return error?.response?.data?.message ?? fallback
    },

    async fetchBoard(): Promise<void> {
      this.loading = true
      try {
        const response = await PipelineService.getBoard()
        this.stages = response.data?.data ?? []
      } catch (error) {
        console.error('Failed to fetch pipeline board:', error)
        toast.error('Failed to fetch pipeline board')
      } finally {
        this.loading = false
      }
    },

    async moveCard(leadId: number, toStageId: number): Promise<void> {
      const fromStage = this.stages.find(s => s.leads.some(l => l.id === leadId))
      const toStage = this.stages.find(s => s.id === toStageId)

      if (!fromStage || !toStage) return

      const lead = fromStage.leads.find(l => l.id === leadId)!
      fromStage.leads = fromStage.leads.filter(l => l.id !== leadId)
      fromStage.leads_count--
      toStage.leads.push({ ...lead, pipeline_stage_id: toStageId })
      toStage.leads_count++

      try {
        await PipelineService.moveCard(leadId, toStageId)
      } catch (error) {
        console.error('Failed to move card:', error)
        toast.error('Failed to move card')
        await this.fetchBoard()
      }
    },

    async addStage(name: string): Promise<void> {
      try {
        const response = await PipelineService.addStage(name)
        const newStage = response.data?.data
        this.stages.push({ ...newStage, leads: [], leads_count: 0 })
        toast.success('Column created successfully')
      } catch (error) {
        console.error('Failed to add stage:', error)
        toast.error(this.errorMessage(error, 'Failed to add stage'))
        throw error
      }
    },

    async updateStage(id: number, name: string): Promise<void> {
      try {
        await PipelineService.updateStage(id, name)
        const stage = this.stages.find(s => s.id === id)
        if (stage) stage.name = name
        toast.success('Column updated successfully')
      } catch (error) {
        console.error('Failed to update stage:', error)
        toast.error(this.errorMessage(error, 'Failed to update stage'))
        throw error
      }
    },

    async deleteStage(id: number): Promise<void> {
      try {
        await PipelineService.deleteStage(id)
        this.stages = this.stages.filter(s => s.id !== id)
        toast.success('Column deleted successfully')
      } catch (error) {
        console.error('Failed to delete stage:', error)
        toast.error(this.errorMessage(error, 'Not allowed to delete'))
        throw error
      }
    },

    async reorderStages(items: ReorderStageItem[]): Promise<void> {
      try {
        await PipelineService.reorderStages(items)
      } catch (error) {
        console.error('Failed to reorder stages:', error)
        await this.fetchBoard()
      }
    },

    async updateCard(leadId: number, payload: Record<string, any>): Promise<void> {
      try {
        const response = await PipelineService.updateCard(leadId, payload)
        const updated = response.data?.data

        if (payload.pipeline_stage_id) {
          await this.moveCard(leadId, payload.pipeline_stage_id)
        }

        this.stages = this.stages.map(s => ({
          ...s,
          leads: s.leads.map(l => l.id === leadId ? { ...l, ...updated } : l)
        }))
      } catch (error) {
        console.error('Failed to update card:', error)
        throw error
      }
    },

    async removeCard(leadId: number): Promise<void> {
      try {
        await PipelineService.removeCard(leadId)
        this.stages = this.stages.map(s => ({
          ...s,
          leads: s.leads.filter(l => l.id !== leadId),
          leads_count: s.leads.some(l => l.id === leadId) ? s.leads_count - 1 : s.leads_count
        }))
      } catch (error) {
        console.error('Failed to remove card:', error)
        throw error
      }
    },

    setDragging(lead: PipelineLead | null, fromStageId: number | null): void {
      this.draggingLead = lead
      this.draggingFromStageId = fromStageId
    },

    resetCardForm(): void {
      this.cardForm = {
        name: '',
        interest: '',
        estimated_value: 0,
        notes: '',
        pipeline_stage_id: 0,
        other_interest: ''
      }
    },
  }
})
